import {
  LivestockInsuranceTypes,
  RmaLgmCommodityCodes,
  RmaLrpCommodityCodes,
} from '@harvestiq/constants';
import { zDecimalNullable, zDecimalRequired, zIsoDate } from '@harvestiq/zod';
import { z } from '@harvestiq/zod';
import { emailPolicyDataSchema } from './email-policy-data';
import { recipientSchema } from '../users/recipients';
import { lgmDetailsMarketingMonthsSchema } from './coverage-details';

const coverageIndemnityLgmMarketingMonthSchema =
  // Extend the DB LGM Details schema with fields added from calculation of indemnity
  lgmDetailsMarketingMonthsSchema.extend({
    actualGrossMargin: zDecimalNullable().optional(),
    projectedGrossMargin: zDecimalRequired(),
    actualIndemnity: zDecimalNullable().optional(),
    projectedGrossMarginChange: zDecimalRequired(),
  });

// Coverage Email Data
export const coverageIndemnityDataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(LivestockInsuranceTypes.LRP),
    coverageId: z.string(),
    salesEffectiveDate: zIsoDate(),
    assignedAgent: z
      .object({
        email: z.string(),
        name: z.string(),
        phone: z.string().optional(),
      })
      .optional(),
    coveragePricePerHead: z.number(), // ?decimal?
    targetWeight: z.coerce.number(),
    endDate: zIsoDate(),
    endorsementLength: z.number(),
    numberOfHead: z.coerce.number(), // can be string or number in DB
    actualEndingValuePerHead: z.number(),
    calculatedIndemnityPerHead: z.number(),
    commodityCode: z.nativeEnum(RmaLrpCommodityCodes),
    commodityType: z.string(),
    policy: emailPolicyDataSchema.optional(),
    isPre2026BeginningFarmerRancher: z.boolean().optional(),
    isVeteranFarmerRancher: z.boolean().optional(),
    beginningFarmerRancherCropYearCount: z.number().int().nullable().optional(),
  }),
  z.object({
    type: z.literal(LivestockInsuranceTypes.LGM),
    coverageId: z.string(),
    salesEffectiveDate: zIsoDate(),
    assignedAgent: z
      .object({
        email: z.string(),
        name: z.string(),
        phone: z.string().optional(),
      })
      .optional(),
    endDate: zIsoDate(),
    triggeringEndDate: zIsoDate(),
    deductible: z.number(),
    liveCattleTargetCwtPerHead: z.number().nullable(),
    feederCattleTargetCwtPerHead: z.number().nullable(),
    cornTargetBuPerHead: z.number().nullable(),
    marketingMonths: z.array(coverageIndemnityLgmMarketingMonthSchema),
    grossMarginGuarantee: z.number(),
    totalActualGrossMargin: z.number(),
    projectedIndemnity: z.number(),
    guaranteedGrossMarginPerHead: z.number(),
    grossMarginPerHead: z.number(),
    calculatedIndemnityPerHead: z.number(),
    numberOfHead: z.coerce.number(),
    commodityCode: z.nativeEnum(RmaLgmCommodityCodes),
    commodityType: z.string(),
    policy: emailPolicyDataSchema.optional(),
    isPre2026BeginningFarmerRancher: z.boolean().optional(),
    isVeteranFarmerRancher: z.boolean().optional(),
    beginningFarmerRancherCropYearCount: z.number().int().nullable().optional(),
  }),
]);

export type CoverageIndemnityData = z.infer<typeof coverageIndemnityDataSchema>;

export const coverageIndemnityNotificationSchema = z
  .object({
    to: z.array(recipientSchema),
    cc: z.array(recipientSchema).optional(),
    coverageIndemnityData: coverageIndemnityDataSchema,
  })
  .array();

export type CoverageIndemnityNotification = z.infer<
  typeof coverageIndemnityNotificationSchema
>;
export const coverageIndemnityApiResponseSchema = z.object({
  coverages: coverageIndemnityNotificationSchema,
});

export type CoverageIndemnityApiResponse = z.infer<
  typeof coverageIndemnityApiResponseSchema
>;

export const coverageIndemnityApiRequestSchema = z.object({
  type: z.nativeEnum(LivestockInsuranceTypes),
  daysBefore: z.coerce.number().optional(),
  sendPreview: z.string().optional(),
});

export type CoverageIndemnityApiRequest = z.infer<
  typeof coverageIndemnityApiRequestSchema
>;
